/* Native modal keeps the price guarantee above sticky product content. */
if (!customElements.get('product-price-guarantee')) {
  customElements.define('product-price-guarantee', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-guarantee-open]').addEventListener('click', () => {
        if (!this.dialog.open) this.dialog.showModal();
      }, options);
      this.querySelector('[data-guarantee-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) this.dialog.close();
    }
  });
}

/* Unified product offers; existing gift/discount nodes retain their variant filters. */
if (!customElements.get('product-offers')) {
  customElements.define('product-offers', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll('[data-offer-slide]:not([data-offer-clone])')];
      this.track = this.querySelector('[data-offers-track]');
      this.progress = this.querySelector('[data-offers-progress]');
      this.progressFill = this.querySelector('[data-offers-progress-fill]');
      this.navigation = this.querySelector('[data-offers-navigation]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay !== 'false';
      this.manualPaused ??= false;
      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.hovered = false;
      this.inView = false;
      this.editorSelected = false;
      this.activeSlide = null;
      this.refresh();
      this.navigation.addEventListener('click', (event) => {
        const button = event.target.closest('[data-offer-step]');
        if (!button || !this.navigation.contains(button) || this.visibleSlides.length < 2) return;
        this.manualPaused = true;
        this.show(this.visibleSlides.indexOf(this.activeSlide) + Number(button.dataset.offerStep));
      }, { signal });
      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.settleLoop();
      }, { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.schedule();
      }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => this.schedule(), { signal });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (!signal.aborted) this.schedule();
      }), { signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.manualPaused = true;
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        const index = this.visibleSlides.indexOf(this.activeSlide);
        if (event.key === 'Home') this.show(0);
        else if (event.key === 'End') this.show(this.visibleSlides.length - 1);
        else this.show(index + (event.key === 'ArrowRight' ? direction : -direction));
      }, { signal });
      // Listen on the stationary viewport, not the translated track.
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0) return;
        this.beginSwipe('pointer', event.pointerId, event.clientX, event.clientY, event.target);
        // Stop native image dragging/text selection before it can cancel our gesture.
        if (this.swipe?.kind === 'pointer' && this.swipe.id === event.pointerId && event.cancelable) event.preventDefault();
      }, { signal, passive: false });
      window.addEventListener('pointermove', (event) => {
        if (this.swipe?.kind !== 'pointer' || event.pointerId !== this.swipe.id) return;
        this.moveSwipe(event.clientX, event.clientY, event);
      }, { signal, passive: false });
      window.addEventListener('pointerup', (event) => {
        if (this.swipe?.kind === 'pointer' && this.swipe.id === event.pointerId) this.finishSwipe(true, event.clientX);
      }, { signal });
      window.addEventListener('pointercancel', (event) => {
        if (this.swipe?.kind === 'pointer' && this.swipe.id === event.pointerId) this.finishSwipe(false);
      }, { signal });
      // A capture change is not a cancellation; window still receives move/up events.
      // Explicit touch handling avoids relying on pointer capture for mobile swipes.
      this.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) { this.finishSwipe(false); return; }
        const touch = event.touches[0];
        this.beginSwipe('touch', touch.identifier, touch.clientX, touch.clientY, event.target);
      }, { signal, passive: true });
      this.addEventListener('touchmove', (event) => {
        if (this.swipe?.kind !== 'touch') return;
        if (event.touches.length !== 1) { this.finishSwipe(false); return; }
        const touch = [...event.touches].find(item => item.identifier === this.swipe.id);
        if (touch) this.moveSwipe(touch.clientX, touch.clientY, event);
      }, { signal, passive: false });
      this.addEventListener('touchend', (event) => {
        if (this.swipe?.kind !== 'touch') return;
        const touch = [...event.changedTouches].find(item => item.identifier === this.swipe.id);
        if (touch) this.finishSwipe(true, touch.clientX);
      }, { signal });
      this.addEventListener('touchcancel', () => this.finishSwipe(false), { signal });
      this.addEventListener('wheel', (event) => this.onWheel(event), { signal, passive: false });
      window.addEventListener('blur', () => this.finishSwipe(false), { signal });
      this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { signal, capture: true });
      this.addEventListener('shopify:block:select', (event) => {
        this.editorSelected = true;
        const slide = event.target.closest('[data-offer-slide]');
        const index = this.visibleSlides.indexOf(slide);
        if (index >= 0) this.show(index);
        this.schedule();
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => this.schedule(), { signal });
      this.variantObserver = new MutationObserver(() => this.refresh());
      this.querySelectorAll('[data-gift-variants], [data-discount-variants]').forEach((source) => {
        this.variantObserver.observe(source, { attributes: true, attributeFilter: ['hidden'] });
      });
      this.viewObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.schedule();
      });
      this.viewObserver.observe(this);
    }

    beginSwipe(kind, id, x, y, target) {
      if (this.visibleSlides.length < 2 || this.swipe || target.closest('a, button, input, select, textarea')) return;
      this.settleLoop();
      this.swipe = {
        kind, id, x, y, dx: 0, moved: false,
        width: this.clientWidth,
        direction: getComputedStyle(this).direction === 'rtl' ? -1 : 1
      };
      this.schedule();
    }

    moveSwipe(x, y, event) {
      const dx = x - this.swipe.x;
      const dy = y - this.swipe.y;
      if (!this.swipe.moved) {
        if (this.swipe.kind === 'touch' && Math.abs(dy) >= 8 && Math.abs(dy) > Math.abs(dx) * 1.25) {
          this.finishSwipe(false);
          return;
        }
        if (Math.abs(dx) < 6) return;
        this.swipe.moved = true;
        this.manualPaused = true;
        if (this.swipe.kind === 'pointer') this.setPointerCapture(this.swipe.id);
        this.track.classList.add('is-dragging');
      }
      if (event.cancelable) event.preventDefault();
      this.swipe.dx = Math.max(-this.swipe.width, Math.min(this.swipe.width, dx));
      // High-frequency mouse events share one transform write per display frame.
      if (this.dragFrame == null) {
        this.dragFrame = requestAnimationFrame(() => {
          this.dragFrame = null;
          if (this.swipe) this.position(this.swipe.dx);
        });
      }
    }

    onWheel(event) {
      if (this.visibleSlides.length < 2 || this.swipe || event.ctrlKey ||
          Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      if (event.cancelable) event.preventDefault();
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => { this.wheelDistance = 0; this.wheelHandled = false; }, 200);
      if (this.wheelHandled) return;
      this.manualPaused = true;
      this.schedule();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.clientWidth : 1;
      this.wheelDistance = (this.wheelDistance || 0) + event.deltaX * unit;
      if (Math.abs(this.wheelDistance) < 40) return;
      this.wheelHandled = true;
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.show(this.visibleSlides.indexOf(this.activeSlide) + (this.wheelDistance > 0 ? direction : -direction));
    }

    refresh() {
      this.finishSwipe(false);
      this.settleLoop();
      this.track.querySelectorAll('[data-offer-clone]').forEach((clone) => clone.remove());
      this.visibleSlides = this.slides.filter((slide) => {
        const source = slide.querySelector('[data-gift-variants], [data-discount-variants]');
        return !source || !source.hidden;
      });
      this.hidden = this.visibleSlides.length === 0;
      this.progress.hidden = this.visibleSlides.length <= 1;
      this.classList.toggle('is-carousel', this.visibleSlides.length > 1);
      this.slides.forEach((slide) => { slide.hidden = !this.visibleSlides.includes(slide); });
      this.visibleSlides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} / ${this.visibleSlides.length}`);
      });
      this.navigation.hidden = this.visibleSlides.length <= 1;
      if (this.visibleSlides.length > 1) {
        this.track.prepend(this.cloneSlide(this.visibleSlides[this.visibleSlides.length - 1]));
        this.track.append(this.cloneSlide(this.visibleSlides[0]));
      }
      this.track.style.transition = 'none';
      this.show(Math.max(0, this.visibleSlides.indexOf(this.activeSlide)));
      this.track.getBoundingClientRect();
      this.track.style.removeProperty('transition');
    }

    cloneSlide(slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-offer-clone', '');
      clone.inert = true;
      clone.setAttribute('aria-hidden', 'true');
      // Loop copies are visual only: exclude them from variant filters and editor IDs.
      [clone, ...clone.querySelectorAll('*')].forEach((node) => {
        [...node.attributes].forEach(({ name }) => {
          if (name === 'id' || name === 'data-offer-block' || name === 'data-gift-variants' ||
              name === 'data-discount-variants' || name.startsWith('data-shopify')) node.removeAttribute(name);
        });
      });
      return clone;
    }

    settleLoop() {
      clearTimeout(this.loopTimer);
      if (!this.loopPending) return;
      this.loopPending = false;
      this.visualIndex = this.visibleSlides.indexOf(this.activeSlide) + 1;
      this.track.style.transition = 'none';
      this.position();
      this.track.getBoundingClientRect();
      this.track.style.removeProperty('transition');
    }

    show(index) {
      this.settleLoop();
      const count = this.visibleSlides.length;
      const normalized = count ? ((index % count) + count) % count : 0;
      this.activeSlide = count ? this.visibleSlides[normalized] : null;
      this.visualIndex = count > 1 ? normalized + 1 : 0;
      // Animate to the adjacent end copy, then silently move to its original.
      this.loopPending = count > 1 && (index < 0 || index >= count);
      if (this.loopPending) this.visualIndex = index < 0 ? 0 : count + 1;
      this.slides.forEach((slide) => {
        slide.inert = slide !== this.activeSlide;
        slide.setAttribute('aria-hidden', String(slide !== this.activeSlide));
      });
      this.position();
      if (this.loopPending) {
        if (this.motion.matches) this.settleLoop();
        else this.loopTimer = setTimeout(() => this.settleLoop(), 400);
      }
      this.schedule();
    }

    position(offset = 0) {
      const index = this.visualIndex || 0;
      const direction = this.swipe?.direction ?? (getComputedStyle(this).direction === 'rtl' ? -1 : 1);
      this.track.style.transform = `translateX(calc(${-index * direction * 100}% + ${offset}px))`;
    }

    finishSwipe(commit, releaseX) {
      if (!this.swipe) return;
      const { kind, id, moved, direction, width } = this.swipe;
      const previousDx = this.swipe.dx;
      if (commit && moved && Number.isFinite(releaseX)) {
        this.swipe.dx = Math.max(-width, Math.min(width, releaseX - this.swipe.x));
      }
      const dx = this.swipe.dx;
      if (this.dragFrame != null || dx !== previousDx) {
        cancelAnimationFrame(this.dragFrame);
        this.dragFrame = null;
        // Commit the last drag position before restoring the snap transition.
        this.position(dx);
        this.track.getBoundingClientRect();
      }
      this.swipe = null;
      this.track.classList.remove('is-dragging');
      if (kind === 'pointer' && this.hasPointerCapture(id)) this.releasePointerCapture(id);
      if (moved) {
        this.suppressClick = true;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, kind === 'touch' ? 350 : 0);
      }
      const threshold = Math.min(24, width * 0.08);
      const step = commit && moved && Math.abs(dx) >= threshold ? (dx < 0 ? direction : -direction) : 0;
      this.show(this.visibleSlides.indexOf(this.activeSlide) + step);
    }

    schedule() {
      clearTimeout(this.timer);
      this.progressAnimation?.cancel();
      if (!this.isConnected || !this.autoplay || this.manualPaused || this.swipe || this.hovered || this.editorSelected ||
          this.motion.matches || document.hidden || !this.inView || this.matches(':focus-within') ||
          this.visibleSlides.length < 2) return;
      this.progressAnimation = this.progressFill.animate(
        { transform: ['scaleX(0)', 'scaleX(1)'] },
        { duration: this.interval, easing: 'linear', fill: 'forwards' }
      );
      this.timer = setTimeout(() => this.show(this.visibleSlides.indexOf(this.activeSlide) + 1), this.interval);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.finishSwipe(false);
      this.settleLoop();
      this.track.querySelectorAll('[data-offer-clone]').forEach((clone) => clone.remove());
      clearTimeout(this.clickTimer);
      clearTimeout(this.wheelTimer);
      this.wheelDistance = 0;
      this.wheelHandled = false;
      this.controller = null;
      this.variantObserver?.disconnect();
      this.viewObserver?.disconnect();
      clearTimeout(this.timer);
      this.progressAnimation?.cancel();
    }
  });
}

/* Product variant picker. Cart submission and media playback use existing theme components. */
if (!customElements.get('product-variants')) {
  class ProductVariants extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.groups = [...this.querySelectorAll('fieldset')];
      this.section = document.getElementById(`shopify-section-${this.dataset.section}`);
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.status = this.querySelector('[role="status"]');
      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.setupPackageDrag();
      this.updateAvailability();
      this.updateGalleryVisibility(this.variants.find(variant => variant.options.every((value, index) => value === this.options[index])), false);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.packageDragEvents?.abort();
      this.finishPackageDrag?.();
      clearTimeout(this.packageClickTimer);
      this.request?.abort();
      this.requestId = (this.requestId || 0) + 1;
    }

    setupPackageDrag() {
      this.packageDragEvents?.abort();
      this.packageDragEvents = new AbortController();
      const options = { signal: this.packageDragEvents.signal };
      this.querySelectorAll('.product__option--packages-slider .product__option-values').forEach(track => {
        let drag;
        let suppressClick = false;
        const finish = () => {
          if (!drag) return;
          const { id, moved } = drag;
          drag = null;
          track.classList.remove('is-dragging');
          if (track.hasPointerCapture(id)) track.releasePointerCapture(id);
          if (moved) {
            clearTimeout(this.packageClickTimer);
            this.packageClickTimer = setTimeout(() => { suppressClick = false; }, 0);
          }
        };
        this.finishPackageDrag = finish;
        track.addEventListener('pointerdown', event => {
          // Touch devices retain native scrolling and momentum.
          if (event.pointerType !== 'mouse' || event.button !== 0 || track.scrollWidth <= track.clientWidth) return;
          clearTimeout(this.packageClickTimer);
          suppressClick = false;
          drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
        }, options);
        window.addEventListener('pointermove', event => {
          if (!drag || event.pointerId !== drag.id) return;
          const distance = event.clientX - drag.x;
          if (!drag.moved && Math.abs(distance) < 6) return;
          if (!drag.moved) {
            drag.moved = true;
            suppressClick = true;
            track.classList.add('is-dragging');
            track.setPointerCapture(event.pointerId);
          }
          event.preventDefault();
          track.scrollLeft = drag.left - distance;
        }, options);
        window.addEventListener('pointerup', finish, options);
        window.addEventListener('pointercancel', finish, options);
        window.addEventListener('blur', finish, options);
        track.addEventListener('lostpointercapture', finish, options);
        track.addEventListener('dragstart', event => event.preventDefault(), options);
        track.addEventListener('click', event => {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, { ...options, capture: true });
      });
    }

    get options() {
      return this.groups.map(group => group.querySelector('input:checked')?.value);
    }

    // Preserve a valid choice; only replace downstream choices if the combination is absent.
    static resolveVariant(variants, options, changedIndex) {
      const exact = variants.find(variant => variant.options.every((value, index) => value === options[index]));
      if (exact) return exact;
      const candidates = variants.filter(variant => variant.options.every((value, index) => index > changedIndex || value === options[index]));
      return candidates.find(variant => variant.available) || candidates[0];
    }

    updateAvailability() {
      const options = this.options;
      this.groups.forEach((group, index) => {
        group.querySelectorAll('input').forEach(input => {
          const candidates = this.variants.filter(variant =>
            variant.options[index] === input.value &&
            variant.options.every((value, previous) => previous >= index || value === options[previous])
          );
          input.disabled = candidates.length === 0;
          input.toggleAttribute('data-sold-out', !candidates.some(variant => variant.available));
          if (group.classList.contains('product__option--packages')) {
            const candidate = candidates.find(variant => variant.available) || candidates[candidates.length - 1];
            input.closest('.product__option-item').hidden = !candidate;
          }
        });
      });
    }

    updateGalleryVisibility(variant, showVariantThumbnail = true) {
      const gallery = this.section.querySelector('media-gallery');
      if (!gallery) return;
      const variantMedia = new Set(this.variants.filter(item => item.featured_media).map(item => `${this.dataset.section}-${item.featured_media.id}`));
      const selectedMedia = showVariantThumbnail && variant?.featured_media
        ? `${this.dataset.section}-${variant.featured_media.id}`
        : gallery.querySelector('.product__media-item.is-active[data-media-id]')?.dataset.mediaId;
      gallery.querySelectorAll('.product__media-item[data-media-id]').forEach(item => {
        item.hidden = variantMedia.has(item.dataset.mediaId) && item.dataset.mediaId !== selectedMedia;
      });
      gallery.querySelectorAll('.thumbnail-list__item[data-target]').forEach(item => {
        const mediaId = item.dataset.target;
        item.hidden = variantMedia.has(mediaId) && (!showVariantThumbnail || mediaId !== selectedMedia);
      });
      gallery.querySelector('product-gallery')?.resetPages();
      const thumbnails = gallery.querySelector('thumbnail-slider');
      if (thumbnails) {
        const count = thumbnails.querySelectorAll('.thumbnail-list__item:not([hidden])').length;
        thumbnails.slider.dataset.mediaCount = count;
        thumbnails.classList.toggle('hidden', count < 2);
        thumbnails.resetPages();
      }
    }

    async onChange(event) {
      if (!event.target.matches('input[type="radio"]')) return;
      const index = Number(event.target.closest('fieldset').dataset.optionIndex);
      const variant = ProductVariants.resolveVariant(this.variants, this.options, index);
      const requestId = this.requestId = (this.requestId || 0) + 1;
      this.request?.abort();
      this.setPurchaseState(false, '', true);
      this.section.querySelector('product-form')?.handleErrorMessage();
      if (!variant) {
        this.setPurchaseState(false, window.variantStrings.unavailable);
        this.updateAvailability();
        this.removeAttribute('aria-busy');
        this.status.textContent = window.variantStrings.unavailable;
        return;
      }

      this.groups.forEach((group, optionIndex) => {
        group.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.options[optionIndex]; });
      });
      this.updateAvailability();
      const variantInput = this.form?.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.section.querySelectorAll('[data-gift-variants], [data-discount-variants]').forEach(block => {
        const ids = (block.dataset.giftVariants || block.dataset.discountVariants || '').split(',').map(id => id.trim()).filter(Boolean);
        block.hidden = ids.length > 0 && !ids.includes(String(variant.id));
      });
      const gallery = this.section.querySelector('media-gallery');
      this.updateGalleryVisibility(variant);
      if (variant.featured_media && gallery?.setActiveMedia) {
        gallery.setActiveMedia(`${this.dataset.section}-${variant.featured_media.id}`, true, false);
        gallery.querySelector('thumbnail-slider')?.resetPages();
      }
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', pageUrl);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));

      this.request = new AbortController();
      const requestUrl = new URL(this.dataset.url, window.location.origin);
      // Preserve the alternate template and preview context used for product previews.
      pageUrl.searchParams.forEach((value, key) => requestUrl.searchParams.set(key, value));
      requestUrl.searchParams.set('section_id', this.dataset.section);
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      try {
        const response = await fetch(requestUrl, { signal: this.request.signal });
        if (!response.ok) throw new Error(`Variant update failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (requestId !== this.requestId || !this.isConnected) return;
        const sourcePicker = html.querySelector('product-variants');
        if (!sourcePicker) throw new Error('Product section was not returned');
        const price = this.section.querySelector(`#price-${this.dataset.section}`);
        const nextPrice = html.getElementById(`price-${this.dataset.section}`);
        if (price && nextPrice) price.innerHTML = nextPrice.innerHTML;
        // Replace label content, keeping radio nodes and keyboard focus intact.
        this.querySelectorAll('label[for]').forEach(label => {
          const sourceInput = html.getElementById(label.htmlFor);
          const sourceLabel = sourceInput?.nextElementSibling;
          if (sourceLabel?.tagName === 'LABEL') label.innerHTML = sourceLabel.innerHTML;
        });
        const quantity = this.form?.querySelector('[name="quantity"]');
        const nextForm = html.getElementById(`product-form-${this.dataset.section}`);
        const button = this.form?.querySelector('[name="add"]');
        const nextButton = nextForm?.querySelector('[name="add"]');
        if (button && nextButton) button.dataset.price = nextButton.dataset.price;
        const nextQuantity = nextForm?.querySelector('[name="quantity"]');
        if (quantity && nextQuantity) {
          ['min', 'max', 'step'].forEach(attribute => {
            if (nextQuantity.hasAttribute(attribute)) quantity.setAttribute(attribute, nextQuantity.getAttribute(attribute));
            else quantity.removeAttribute(attribute);
          });
          const min = Number(nextQuantity.value) || Number(quantity.min) || 1;
          const max = quantity.max ? Number(quantity.max) : Infinity;
          const step = Number(quantity.step) || 1;
          const current = Number(quantity.value) || min;
          quantity.value = Math.max(min, Math.min(max, min + Math.floor((current - min) / step) * step));
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.setPurchaseState(variant.available);
        this.status.textContent = variant.available ? variant.title : window.variantStrings.soldOut;
        document.dispatchEvent(new CustomEvent('productInfo:loaded'));
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestId || !this.isConnected) return;
        // Keep purchase disabled until the selected variant's price has been confirmed.
      } finally {
        if (requestId === this.requestId) this.removeAttribute('aria-busy');
      }
    }

    setPurchaseState(available, unavailableText = window.variantStrings.soldOut, pending = false) {
      const button = this.form?.querySelector('[name="add"]');
      if (button) {
        button.disabled = !available;
        button.textContent = available
          ? `${button.dataset.preOrder === 'true' ? window.variantStrings.preOrder : window.variantStrings.addToCart} | ${button.dataset.price}`
          : (unavailableText || button.textContent);
      }
      const checkout = this.form?.querySelector('.product__checkout');
      if (checkout) {
        // Keep the rendered payment area in flow while Shopify refreshes the button.
        checkout.style.setProperty('--checkout-height', `${checkout.getBoundingClientRect().height}px`);
        checkout.inert = !available;
        checkout.setAttribute('aria-disabled', String(!available));
        if (!pending) checkout.toggleAttribute('data-unavailable', !available);
      }
    }
  }
  customElements.define('product-variants', ProductVariants);
}

/* Animated, exclusive accordions for product information. */
if (!customElements.get('product-accordion')) {
  class ProductAccordion extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.expanded = this.details.open;
      this.summary.setAttribute('aria-expanded', String(this.expanded));
      this.onClick = event => {
        event.preventDefault();
        const expanded = !this.expanded;
        if (expanded) {
          this.closest('.product__info').querySelectorAll('product-accordion').forEach(other => {
            if (other !== this && other.expanded) other.setExpanded(false);
          });
        }
        this.setExpanded(expanded);
      };
      this.summary.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.summary.removeEventListener('click', this.onClick);
      this.animation?.cancel();
      this.finish?.();
    }

    setExpanded(expanded) {
      const details = this.details;
      const start = details.getBoundingClientRect().height;
      this.animation?.cancel();
      this.expanded = expanded;
      details.classList.toggle('is-collapsing', !expanded);
      this.summary.setAttribute('aria-expanded', String(expanded));
      details.open = true;
      details.style.height = '';
      const end = expanded ? details.getBoundingClientRect().height : this.summary.getBoundingClientRect().height;
      this.finish = () => {
        details.open = expanded;
        details.classList.remove('is-collapsing');
        details.style.height = '';
        details.style.overflow = '';
        this.animation = null;
        this.finish = null;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !details.animate) {
        this.finish();
        return;
      }
      details.style.overflow = 'hidden';
      this.animation = details.animate(
        { height: [`${start}px`, `${end}px`] },
        { duration: 250, easing: 'ease-in-out' }
      );
      this.animation.onfinish = this.finish;
    }
  }
  customElements.define('product-accordion', ProductAccordion);
}

// A compact scrollbar shared by touch scrolling, thumbnail navigation and keyboard input.
if (!customElements.get('product-thumbnail-scrollbar')) {
  customElements.define('product-thumbnail-scrollbar', class extends HTMLElement {
    connectedCallback() {
      this.slider = this.closest('media-gallery').querySelector('.thumbnail-list');
      this.input = this.querySelector('input');
      this.sync = () => {
        const distance = this.slider.scrollWidth - this.slider.clientWidth;
        this.hidden = distance <= 1 || !this.slider.clientWidth;
        this.input.value = distance > 0 ? Math.round(this.slider.scrollLeft / distance * 1000) : 0;
      };
      this.onInput = () => {
        this.slider.scrollTo({
          left: Number(this.input.value) / 1000 * (this.slider.scrollWidth - this.slider.clientWidth),
          behavior: 'instant'
        });
      };
      this.slider.addEventListener('scroll', this.sync, { passive: true });
      this.input.addEventListener('input', this.onInput);
      this.resizeObserver = new ResizeObserver(this.sync);
      this.resizeObserver.observe(this.slider);
      this.mutationObserver = new MutationObserver(this.sync);
      this.mutationObserver.observe(this.slider, { subtree: true, attributes: true, attributeFilter: ['hidden'], childList: true });
      this.sync();
    }

    disconnectedCallback() {
      this.slider.removeEventListener('scroll', this.sync);
      this.input.removeEventListener('input', this.onInput);
      this.resizeObserver.disconnect();
      this.mutationObserver.disconnect();
    }
  });
}

/* Preorder notices and customer videos used by the main product section. */
if (!customElements.get('product-stock')) {
  customElements.define('product-stock', class ProductStock extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.states = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.update(this.dataset.selectedVariant);
      document.addEventListener('variant:change', event => {
        const variant = event.detail?.variant;
        if (!variant || !Object.hasOwn(this.states, String(variant.id))) return;
        const selectedInput = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="id"]');
        if (selectedInput && selectedInput.value !== String(variant.id)) return;
        this.update(variant.id);
      }, { signal: this.events.signal });
    }

    disconnectedCallback() {
      this.events?.abort();
    }

    update(variantId) {
      const state = this.states[String(variantId)];
      this.hidden = !state?.visible;
      const progress = this.querySelector('[data-stock-progress]');
      progress.hidden = !state?.showProgress;
      this.querySelector('progress').value = state?.percentage || 0;
      this.querySelector('[data-stock-percentage]').textContent = state?.percentage || 0;
      const date = this.querySelector('[data-stock-date]');
      if (date) date.textContent = state?.deliveryDate || '';
    }
  });
}

if (!customElements.get('product-videos')) {
  customElements.define('product-videos', class ProductVideos extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.previews = [...this.querySelectorAll('[data-video-previews] video')];
      this.videos = [...this.querySelectorAll('[data-video-player] video')];
      this.activeIndex = 0;
      this.querySelectorAll('[data-video-index]').forEach(button => {
        button.addEventListener('click', () => this.open(Number(button.dataset.videoIndex)), options);
      });
      this.querySelector('[data-video-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.videos.forEach(video => video.pause()), options);
      if (typeof Swiper !== 'undefined') {
        this.previewSlider = new Swiper(this.querySelector('[data-video-previews]'), {
          speed: 400,
          spaceBetween: 10,
          slidesPerView: 2.6,
          breakpoints: { 750: { slidesPerView: 4 } }
        });
      }
      this.observer = new IntersectionObserver(entries => {
        const visible = entries.some(entry => entry.isIntersecting);
        if (visible && !this.dialog.open && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.previews[0]?.play()?.catch(() => {});
        } else this.previews.forEach(video => video.pause());
      }, { threshold: 0.1 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.previews?.forEach(video => video.pause());
      this.videos?.forEach(video => video.pause());
      if (this.dialog?.open) this.dialog.close();
      this.previewSlider?.destroy(true, true);
      this.playerSlider?.destroy(true, true);
      this.previewSlider = null;
      this.playerSlider = null;
    }

    open(index) {
      this.previews.forEach(video => video.pause());
      if (!this.dialog.open) this.dialog.showModal();
      if (!this.playerSlider && typeof Swiper !== 'undefined') {
        this.playerSlider = new Swiper(this.querySelector('[data-video-player]'), {
          slidesPerView: 1,
          initialSlide: index,
          speed: 400,
          spaceBetween: 2,
          direction: 'vertical',
          breakpoints: { 750: { direction: 'horizontal', spaceBetween: 5 } },
          navigation: { nextEl: this.querySelector('[data-video-next]'), prevEl: this.querySelector('[data-video-prev]') },
          on: { slideChange: slider => this.play(slider.activeIndex) }
        });
      }
      if (this.playerSlider) {
        this.playerSlider.update();
        this.playerSlider.slideTo(index, 0);
      } else {
        this.querySelectorAll('.product__video-slide').forEach((slide, slideIndex) => { slide.hidden = slideIndex !== index; });
        this.querySelectorAll('[data-video-prev], [data-video-next]').forEach(button => { button.hidden = true; });
      }
      this.play(index);
    }

    play(index) {
      this.activeIndex = index;
      this.videos.forEach((video, videoIndex) => {
        if (videoIndex === index && this.dialog.open) video.play()?.catch(() => {});
        else video.pause();
      });
    }

  });
}
